import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'slices';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import BreadCrumb from 'Components/Common/BreadCrumb';
import { useTranslation } from 'react-i18next';
import {
  setActiveBreakingTimeAttendanceId,
  setActiveEditWorkingTimeId,
  setBreakingTimeModalOpen,
  setBreakingTimeValues,
  setCurrentPage,
  setEditWorkingTimeValues,
  setEntriesPerPage,
  setInputPageValue,
  setPageFiltersBranch,
  setWorkingTimeModalOpen,
} from 'slices/timeAttendance/reducer';
import { useDispatch } from 'react-redux';
import { transformArrayToStr } from 'utils/common/handleSelect';
import TableWrapper from 'Components/pos2u/TableWrapper';
import TimeAttendanceSearchForm from 'pages/TimeAttendance/TimeAttendanceSearchForm';
import {
  BreakingTimeResponseData,
  TimeAttendanceLightFromAPI,
} from 'types/timeAttendance/timeAttendanceTypes';
import {
  getBreakingTimeById,
  getTimeAttendanceDailyExport,
  getTimeAttendanceList,
  getTimeAttendanceMonthlyExport,
  getWorkingTimeById,
} from 'services/timeAttendanceService';
import { toast, ToastContainer } from 'react-toastify';
import useWindowSize from 'Components/Hooks/WindowSizeHook';
import {
  getTimeAttendanceColumnWidth,
  TimeAttendanceListActionButtons,
  timeAttendanceTableColumnConfig,
} from './constants';
import { getStoreNameByID } from 'utils/stores/converter';
import IconActionButton from 'Components/pos2u/Buttons/IconActionButton';
import { FaRegClock, FaRegPenToSquare } from 'react-icons/fa6';
import {
  mapApiToBreakingTimeRows,
  mapApiToWokingTimeForm,
} from 'utils/mappers/timeAttendanceMapper';
import BreakingTimeModal from 'pages/TimeAttendance/BreakingTimeModal';
import WorkingTimeModal from 'pages/TimeAttendance/WorkingTimeModal';
import ExportButton from 'pages/TimeAttendance/TimeAttendanceExport/ExportButton';
import { exportTimeAttendanceDailyExcel } from 'Components/pos2u/Excel/exportTimeAttendanceDaily';
import { EXPORT_LIMIT } from 'pages/TimeAttendance/TimeAttendanceExport/constants';
import { exportTimeAttendanceMonthlyExcel } from 'Components/pos2u/Excel/exportTimeAttendanceMonthly';
import { displayDateTime, hoursToHHMM } from 'utils/common/Time';

const TimeAttendancePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const columnHelper = createColumnHelper<TimeAttendanceLightFromAPI>();

  const { layoutModeType } = useSelector(
    (state: RootState) => ({
      layoutModeType: state.Layout.layoutModeType,
    }),
    shallowEqual
  );
  const isDarkMode = layoutModeType === 'dark';

  const timeAttendanceState = useSelector(
    (state: RootState) => state.TimeAttendance
  );
  const { PageFilters, PageDetails } = timeAttendanceState;

  const infoState = useSelector((state: RootState) => state.Info);
  const { merchant, user } = infoState;
  const [timeAttendanceList, setTimeAttendanceList] = useState<
    TimeAttendanceLightFromAPI[]
  >([]);
  const [timeAttendanceTotal, setTimeAttendanceTotal] = useState<number>(0);
  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isExportingDaily, setIsExportingDaily] = useState(false);
  const [isExportingMonthly, setIsExportingMonthly] = useState(false);
  // const [isMonthlyModalOpen, setIsMonthlyModalOpen] = useState(false);

  const { branches, isBranchesLoaded, StoresInfo } = useSelector(
    (state: RootState) => ({
      branches: state.Info.branches,
      isBranchesLoaded: state.Info.isBranchesLoaded,
      StoresInfo: state.Info.StoresInfo,
    }),
    shallowEqual
  );

  const totalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(timeAttendanceTotal / Math.max(1, PageDetails.entriesPerPage))
      ),
    [timeAttendanceTotal, PageDetails.entriesPerPage]
  );

  const timeAttendanceFields: (keyof TimeAttendanceLightFromAPI)[] = useMemo(
    () => ['office_code', 'full_name'],
    []
  );
  const timeAttendanceFields_2: (keyof TimeAttendanceLightFromAPI)[] = useMemo(
    () => [
      'clock_in_at',
      'clock_out_at',
      // 'working_minutes',
      // 'breaking_minutes',
    ],
    []
  );

  const timeAttendanceFields_3: (keyof TimeAttendanceLightFromAPI)[] = useMemo(
    () => ['working_hours', 'breaking_hours'],
    []
  );

  const fetchBreakingTimeByAttendanceId = useCallback(async (id: number) => {
    try {
      const rs = await getBreakingTimeById(id);
      if (rs.message === 'OK' && rs.error_code === 0) {
        return rs.data as BreakingTimeResponseData;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch Breaking Time By Id:', error);
      return null;
    }
  }, []);

  const fetchWorkingTimeById = useCallback(async (id: number) => {
    try {
      const rs = await getWorkingTimeById(id);
      if (rs.message === 'OK' && rs.error_code === 0) {
        return rs.data;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch Customer By Id:', error);
      return null;
    }
  }, []);

  const Columns: ColumnDef<TimeAttendanceLightFromAPI, any>[] = useMemo(
    () => [
      ...timeAttendanceFields.map((field) =>
        columnHelper.accessor(field, {
          header: () => (
            <div
              className={`${timeAttendanceTableColumnConfig[field].textColumnStyle}`}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {t(timeAttendanceTableColumnConfig[field].label)}
            </div>
          ),
          cell: (cell) => (
            <div
              className={`${timeAttendanceTableColumnConfig[field].textColumnStyle} px-0 mx-0`}
            >
              <div>{String(cell.getValue() ?? '')}</div>
            </div>
          ),
          meta: {
            cellStyle: {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              alignContent: 'flex-start',
              minWidth: getTimeAttendanceColumnWidth(
                isMobile,
                field,
                'minWidth'
              ),
              maxWidth: getTimeAttendanceColumnWidth(
                isMobile,
                field,
                'maxWidth'
              ),
            },
          },
          enableColumnFilter: false,
          enableSorting: false,
        })
      ),

      columnHelper.display({
        id: `branch_code`,
        header: () => (
          <div
            className={`${timeAttendanceTableColumnConfig['branch_code'].textColumnStyle}`}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {t('Branch')}
          </div>
        ),

        cell: (cell) => (
          <div
            className={`${timeAttendanceTableColumnConfig['branch_code'].textColumnStyle} px-0 mx-0`}
          >
            <div>
              {cell.row.original.store_id
                ? getStoreNameByID(StoresInfo, cell.row.original.store_id)
                : '-'}
            </div>
          </div>
        ),
        meta: {
          cellStyle: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            alignContent: 'flex-start',
            minWidth: getTimeAttendanceColumnWidth(
              isMobile,
              'branch_code',
              'minWidth'
            ),
            maxWidth: getTimeAttendanceColumnWidth(
              isMobile,
              'branch_code',
              'maxWidth'
            ),
          },
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),

      // ...timeAttendanceFields_2.map((field: keyof TimeAttendanceLightFromAPI) =>
      //   columnHelper.accessor(field, {
      //     header: () => (
      //       <div
      //         className={`${timeAttendanceTableColumnConfig[field].textColumnStyle}`}
      //         style={{
      //           whiteSpace: 'pre-wrap',
      //           wordBreak: 'break-word',
      //         }}
      //       >
      //         {t(timeAttendanceTableColumnConfig[field].label)}
      //       </div>
      //     ),
      //     cell: (cell) => (
      //       <div
      //         className={`${timeAttendanceTableColumnConfig[field].textColumnStyle} px-0 mx-0`}
      //       >
      //         {/* <div>{String(cell.getValue() ?? '')}</div> */}
      //         {/* <div>{String(cell.getValue() ?? '')}</div> */}
      //         <div>
      //           {' '}
      //           {['clock_in_at', 'clock_out_at'].includes(field)
      //             ? displayDateTime(cell.getValue())
      //             : String(cell.getValue() ?? '')}
      //         </div>
      //       </div>
      //     ),
      //     meta: {
      //       cellStyle: {
      //         whiteSpace: 'pre-wrap',
      //         wordBreak: 'break-word',
      //         alignContent: 'flex-start',
      //         minWidth: getTimeAttendanceColumnWidth(
      //           isMobile,
      //           field,
      //           'minWidth'
      //         ),
      //         maxWidth: getTimeAttendanceColumnWidth(
      //           isMobile,
      //           field,
      //           'maxWidth'
      //         ),
      //       },
      //     },
      //     enableColumnFilter: false,
      //     enableSorting: false,
      //   })
      // ),
      ...timeAttendanceFields_2.map((field: keyof TimeAttendanceLightFromAPI) =>
        columnHelper.accessor(field, {
          header: () => (
            <div
              className={`${timeAttendanceTableColumnConfig[field].textColumnStyle}`}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {t(timeAttendanceTableColumnConfig[field].label)}
            </div>
          ),
          cell: (cell) => (
            <div
              className={`${timeAttendanceTableColumnConfig[field].textColumnStyle} px-0 mx-0`}
            >
              {/* <div>{String(cell.getValue() ?? '')}</div> */}
              {/* <div>{String(cell.getValue() ?? '')}</div> */}
              <div>{displayDateTime(cell.getValue())}</div>
            </div>
          ),
          meta: {
            cellStyle: {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              alignContent: 'flex-start',
              minWidth: getTimeAttendanceColumnWidth(
                isMobile,
                field,
                'minWidth'
              ),
              maxWidth: getTimeAttendanceColumnWidth(
                isMobile,
                field,
                'maxWidth'
              ),
            },
          },
          enableColumnFilter: false,
          enableSorting: false,
        })
      ),
      ...timeAttendanceFields_3.map((field: keyof TimeAttendanceLightFromAPI) =>
        columnHelper.accessor(field, {
          header: () => (
            <div
              className={`${timeAttendanceTableColumnConfig[field].textColumnStyle}`}
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {t(timeAttendanceTableColumnConfig[field].label)}
            </div>
          ),
          cell: (cell) => (
            <div
              className={`${timeAttendanceTableColumnConfig[field].textColumnStyle} px-0 mx-0`}
            >
              {/* <div>{String(cell.getValue() ?? '')}</div> */}
              {/* <div>{String(cell.getValue() ?? '')}</div> */}
              <div>{hoursToHHMM(Number(cell.getValue() || 0))}</div>
            </div>
          ),
          meta: {
            cellStyle: {
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              alignContent: 'flex-start',
              minWidth: getTimeAttendanceColumnWidth(
                isMobile,
                field,
                'minWidth'
              ),
              maxWidth: getTimeAttendanceColumnWidth(
                isMobile,
                field,
                'maxWidth'
              ),
            },
          },
          enableColumnFilter: false,
          enableSorting: false,
        })
      ),
      columnHelper.display({
        id: `note`,
        header: () => (
          <div
            className={`${timeAttendanceTableColumnConfig['note'].textColumnStyle}`}
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {t('Note')}
          </div>
        ),

        cell: (cell) => (
          <div
            className={`${timeAttendanceTableColumnConfig['note'].textColumnStyle} px-0 mx-0`}
          >
            <div>
              {cell.row.original.store_id
                ? getStoreNameByID(StoresInfo, cell.row.original.store_id)
                : '-'}
            </div>
          </div>
        ),
        meta: {
          cellStyle: {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            alignContent: 'flex-start',
            minWidth: getTimeAttendanceColumnWidth(
              isMobile,
              'note',
              'minWidth'
            ),
            maxWidth: getTimeAttendanceColumnWidth(
              isMobile,
              'note',
              'maxWidth'
            ),
          },
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),

      columnHelper.display({
        id: 'action',
        header: () => <div className="text-center">{t('Action')}</div>,
        cell: ({ row }) => {
          const timeAttendance = row.original;
          const toggleId = `dd-menu-${timeAttendance.id}`;

          // const handleMouseEnter = () => {
          //   const toggleButton = document.querySelector(
          //     `[data-toggle="${toggleId}"]`
          //   );
          //   if (toggleButton) (toggleButton as HTMLElement).click();
          // };

          const handleMouseLeave = () => {
            const dropdownMenu = document.querySelector(
              `[data-dropdown-menu="${toggleId}"]`
            );
            if (dropdownMenu) {
              const toggleButton = document.querySelector(
                `[data-toggle="${toggleId}"]`
              );
              if (toggleButton && dropdownMenu.classList.contains('show')) {
                (toggleButton as HTMLElement).click();
              }
            }
          };
          return (
            <ul
              className="list-inline hstack gap-0 mb-0"
              key={`timeAttendance-${timeAttendance.id}`}
              onMouseLeave={handleMouseLeave}
            >
              <li className="list-inline-item">
                <IconActionButton
                  onClick={async () => {
                    dispatch(
                      setActiveEditWorkingTimeId(timeAttendance.id.toString())
                    );
                    const rawValue = await fetchWorkingTimeById(
                      timeAttendance.id
                    );

                    const formValue = mapApiToWokingTimeForm(rawValue);
                    dispatch(setEditWorkingTimeValues(formValue));
                    dispatch(setWorkingTimeModalOpen(true));
                  }}
                  icon={
                    <FaRegPenToSquare
                      fontSize={TimeAttendanceListActionButtons.icon}
                    />
                  }
                  title={t('Edit')}
                  height={TimeAttendanceListActionButtons.button.height}
                  width={TimeAttendanceListActionButtons.button.width}
                />
              </li>
              <li className="list-inline-item">
                <IconActionButton
                  onClick={async () => {
                    dispatch(
                      setActiveBreakingTimeAttendanceId(
                        timeAttendance.id.toString()
                      )
                    );
                    const rawValue = await fetchBreakingTimeByAttendanceId(
                      timeAttendance.id
                    );
                    dispatch(
                      setBreakingTimeValues(mapApiToBreakingTimeRows(rawValue))
                    );
                    dispatch(setBreakingTimeModalOpen(true));
                  }}
                  icon={
                    <FaRegClock
                      fontSize={TimeAttendanceListActionButtons.icon}
                    />
                  }
                  title={t('Break Time')}
                  height={TimeAttendanceListActionButtons.button.height}
                  width={TimeAttendanceListActionButtons.button.width}
                />
              </li>
            </ul>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      }),
    ],
    [
      t,
      // checkedAll,
      // isCheckAll,
      // selectedCheckBoxDelete,
      StoresInfo,
      isMobile,
      timeAttendanceFields,
      timeAttendanceFields_2,
      timeAttendanceFields_3,
      columnHelper,
      dispatch,
      fetchBreakingTimeByAttendanceId,
      fetchWorkingTimeById,
    ]
  );

  const fetchTimeAttendance = useCallback(async () => {
    if (!merchant?.id) return;
    if (PageFilters.branch.length === 0) return;
    if (!PageFilters.startDate || !PageFilters.endDate) return;

    setIsLoadSuccess(false);

    try {
      const response = await getTimeAttendanceList(
        PageFilters.branch && PageFilters.branch.length === 0
          ? `[${merchant.id}]`
          : `[${transformArrayToStr(PageFilters.branch)}]`,
        PageFilters.officeID,
        PageFilters.startDate,
        PageFilters.endDate,
        PageDetails.currentPage + 1,
        PageDetails.entriesPerPage
      );

      if (response) {
        const { message, error_code, data } = response;
        if (message === 'OK' && error_code === 0) {
          const { time_attendance, total } = data;
          setTimeAttendanceTotal(total);
          setTimeAttendanceList(time_attendance || []);
        } else {
          setTimeAttendanceTotal(0);
          setTimeAttendanceList([]);
        }
        setIsLoadSuccess(true);
      } else {
        console.error('No response data returned');
        setTimeAttendanceTotal(0);
        setTimeAttendanceList([]);
        setIsLoadSuccess(true);
      }
    } catch (error) {
      console.error('Failed to fetch time attendance data:', error);
      setTimeAttendanceTotal(0);
      setTimeAttendanceList([]);
      setIsLoadSuccess(true);
    }
  }, [
    PageDetails.currentPage,
    PageDetails.entriesPerPage,
    PageFilters.branch,
    PageFilters.endDate,
    PageFilters.startDate,
    PageFilters.officeID,
    merchant,
  ]);

  useEffect(() => {
    if (user.store_id_login) {
      dispatch(setPageFiltersBranch([user.store_id_login.toString()]));
    }
  }, [user.store_id_login, dispatch]);

  useEffect(() => {
    if (isBranchesLoaded && branches.length > 0) {
      fetchTimeAttendance();
    }
  }, [fetchTimeAttendance, branches.length, isBranchesLoaded]);
  useEffect(() => {
    if (timeAttendanceList) {
      setIsLoadSuccess(true);
    } else {
      setIsLoadSuccess(false);
    }
  }, [timeAttendanceList]);

  const handlePageChange = (newPage: number) => {
    dispatch(setCurrentPage(newPage));
  };

  const handleExportDaily = useCallback(async () => {
    const selectedStoreIds =
      PageFilters.branch.length > 0
        ? PageFilters.branch
        : branches.map((branch) => branch.value);

    if (!PageFilters.startDate || !PageFilters.endDate) {
      toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }

    if (!selectedStoreIds.length) {
      toast.error('กรุณาเลือกสาขา');
      return;
    }

    if (timeAttendanceTotal > EXPORT_LIMIT) {
      toast.error(`Cannot export more than ${EXPORT_LIMIT} rows at once`);
      return;
    }

    setIsExportingDaily(true);

    try {
      const response = await getTimeAttendanceDailyExport(
        `[${transformArrayToStr(selectedStoreIds)}]`,
        PageFilters.officeID,
        PageFilters.startDate,
        PageFilters.endDate
      );

      if (response?.message !== 'OK' || response.error_code !== 0) {
        toast.error('Export failed');
        return;
      }

      const rows = response.data?.time_attendance || [];

      if (!rows.length) {
        toast.info('ไม่พบข้อมูล');
        return;
      }

      await exportTimeAttendanceDailyExcel(
        t,
        rows,
        (storeId: number) => getStoreNameByID(StoresInfo, storeId) || '-',
        PageFilters.startDate,
        PageFilters.endDate
      );
    } catch (error) {
      console.error('Failed to export daily time attendance:', error);
      toast.error('Export failed');
    } finally {
      setIsExportingDaily(false);
    }
  }, [
    PageFilters.branch,
    PageFilters.endDate,
    PageFilters.officeID,
    PageFilters.startDate,
    StoresInfo,
    branches,
    t,
    timeAttendanceTotal,
  ]);

  // const getInitialMonthlyFilterValue = useCallback(() => {
  //   if (
  //     PageFilters.endDate &&
  //     /^\d{4}-\d{2}-\d{2}$/.test(PageFilters.endDate)
  //   ) {
  //     return PageFilters.endDate.slice(0, 7);
  //   }

  //   const now = new Date();
  //   return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
  //     2,
  //     '0'
  //   )}`;
  // }, [PageFilters.endDate]);

  const handleExportMonthly = useCallback(async () => {
    const selectedStoreIds =
      PageFilters.branch.length > 0
        ? PageFilters.branch
        : branches.map((branch) => branch.value);

    if (!PageFilters.startDate || !PageFilters.endDate) {
      toast.error('กรุณาเลือกช่วงวันที่');
      return;
    }

    if (!selectedStoreIds.length) {
      toast.error('กรุณาเลือกสาขา');
      return;
    }

    if (timeAttendanceTotal > EXPORT_LIMIT) {
      toast.error(`Cannot export more than ${EXPORT_LIMIT} rows at once`);
      return;
    }
    setIsExportingMonthly(true);

    try {
      const response = await getTimeAttendanceMonthlyExport(
        `[${transformArrayToStr(selectedStoreIds)}]`,
        PageFilters.officeID,
        PageFilters.startDate,
        PageFilters.endDate
      );

      if (response?.message !== 'OK' || response.error_code !== 0) {
        // toast.error('Export failed');
        toast.error(t(response?.message));
        return;
      }

      const rows = response.data?.time_attendance || [];

      if (!rows.length) {
        toast.info('ไม่พบข้อมูล');
        return;
      }

      await exportTimeAttendanceMonthlyExcel(
        rows,
        (branchCode: string) =>
          StoresInfo.find((store) => store.code === branchCode)?.name || '-',
        PageFilters.startDate,
        PageFilters.endDate
      );
      // setIsMonthlyModalOpen(false);
    } catch (error) {
      console.error('Failed to export monthly time attendance:', error);
      toast.error('Export failed');
    } finally {
      setIsExportingMonthly(false);
    }
  }, [
    PageFilters.branch,
    PageFilters.officeID,
    PageFilters.startDate,
    PageFilters.endDate,
    StoresInfo,
    branches,
    t,
    timeAttendanceTotal,
  ]);

  //? with monthly modal
  // const handleExportMonthly = useCallback(
  //   async (yyyymm: string) => {
  //     const selectedStoreIds =
  //       PageFilters.branch.length > 0
  //         ? PageFilters.branch
  //         : branches.map((branch) => branch.value);

  //     if (!selectedStoreIds.length) {
  //       toast.error('กรุณาเลือกสาขา');
  //       return;
  //     }

  //     setIsExportingMonthly(true);

  //     try {
  //       const response = await getTimeAttendanceMonthlyExport(
  //         `[${transformArrayToStr(selectedStoreIds)}]`,
  //         yyyymm,
  //         PageFilters.officeID
  //       );

  //       if (response?.message !== 'OK' || response.error_code !== 0) {
  //         // toast.error('Export failed');
  //         toast.error(t(response?.message));
  //         return;
  //       }

  //       const rows = response.data?.time_attendance || [];

  //       if (!rows.length) {
  //         toast.info('ไม่พบข้อมูล');
  //         return;
  //       }

  //       await exportTimeAttendanceMonthlyExcel(
  //         rows,
  //         (branchCode: string) =>
  //           StoresInfo.find((store) => store.code === branchCode)?.name || '-',
  //         yyyymm
  //       );
  //       setIsMonthlyModalOpen(false);
  //     } catch (error) {
  //       console.error('Failed to export monthly time attendance:', error);
  //       toast.error('Export failed');
  //     } finally {
  //       setIsExportingMonthly(false);
  //     }
  //   },
  //   [PageFilters.branch, PageFilters.officeID, StoresInfo, branches, t]
  // );

  document.title = t('Time Attendance List') + ' | POS2U';
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t('Time Attendance List')}
          pageTitle={t('Time Attendance List')}
        />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">
                      {t('Search Time Attendance')}
                    </h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <ExportButton
                        onExportDaily={handleExportDaily}
                        // onExportMonthly={() => setIsMonthlyModalOpen(true)}
                        onExportMonthly={handleExportMonthly}
                        isExporting={isExportingDaily || isExportingMonthly}
                      />
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody>
                <TimeAttendanceSearchForm
                  isDarkMode={isDarkMode}
                  onSubmit={() => undefined}
                  selectedCheckBoxDelete={0}
                />

                <TableWrapper
                  columns={Columns}
                  data={timeAttendanceList}
                  customPageSize={PageDetails.entriesPerPage}
                  divClass="table-responsive table-card mb-1 mt-0"
                  tableClass="align-middle table-nowrap"
                  theadClass={`table-${
                    isDarkMode ? 'dark' : 'light'
                  } text-muted text-uppercase`}
                  trClass=""
                  thClass=""
                  TotalData={timeAttendanceTotal}
                  totalPages={totalPages}
                  currentPage={PageDetails.currentPage}
                  setEntriesPerPage={setEntriesPerPage}
                  handlePageChange={handlePageChange}
                  inputPageValue={PageDetails.inputPageValue}
                  setInputPageValue={setInputPageValue}
                  isTableLoadSuccess={isLoadSuccess}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
        <BreakingTimeModal fetchTimeAttendance={fetchTimeAttendance} />
        <WorkingTimeModal fetchTimeAttendance={fetchTimeAttendance} />
        {/* <ExportMonthlyModal
          show={isMonthlyModalOpen}
          onCloseClick={() => {
            if (!isExportingMonthly) setIsMonthlyModalOpen(false);
          }}
          onConfirmClick={handleExportMonthly}
          initialMonth={getInitialMonthlyFilterValue()}
        /> */}
        <ToastContainer closeButton={false} limit={1} />
      </Container>
    </div>
  );
};

export default TimeAttendancePage;
